import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { AnimatePresence, motion } from 'framer-motion'
import { CheckCircle2, Info, XCircle } from 'lucide-react'

type Tone = 'success' | 'error' | 'info'

interface ToastItem {
  id: number
  tone: Tone
  title: string
  description?: string
}

interface ToastApi {
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
  info: (title: string, description?: string) => void
}

const ToastContext = createContext<ToastApi | null>(null)

let nextId = 1

const icons: Record<Tone, ReactNode> = {
  success: <CheckCircle2 className="size-4 text-status-success" />,
  error: <XCircle className="size-4 text-status-danger" />,
  info: <Info className="size-4 text-status-processing" />,
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const push = useCallback((tone: Tone, title: string, description?: string) => {
    const id = nextId++
    setToasts((list) => [...list.slice(-4), { id, tone, title, description }])
    window.setTimeout(() => {
      setToasts((list) => list.filter((t) => t.id !== id))
    }, tone === 'error' ? 7000 : 4000)
  }, [])

  const api = useMemo<ToastApi>(
    () => ({
      success: (t, d) => push('success', t, d),
      error: (t, d) => push('error', t, d),
      info: (t, d) => push('info', t, d),
    }),
    [push],
  )

  // Slides down under the phone bar, in from the right on wider screens.
  const offscreen = window.matchMedia('(min-width: 640px)').matches ? { x: 24 } : { y: -12 }

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        // On a phone the toast drops in under the 56px bar, never over it:
        // the bar is mostly the cart badge, which is the real confirmation
        // of an add. From sm up it keeps its top right corner.
        <div className="pointer-events-none fixed inset-x-4 top-[calc(4rem+env(safe-area-inset-top))] z-[60] flex flex-col gap-2 sm:left-auto sm:top-4 sm:w-80">
          <AnimatePresence>
            {toasts.map((toast) => (
              <motion.div
                key={toast.id}
                initial={{ opacity: 0, ...offscreen }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                exit={{ opacity: 0, ...offscreen }}
                className="pointer-events-auto rounded-lg border border-line bg-surface p-3 shadow-lg"
              >
                <div className="flex items-start gap-2">
                  {icons[toast.tone]}
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-ink">{toast.title}</p>
                    {toast.description && <p className="mt-0.5 text-xs text-muted">{toast.description}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}
